import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { FindingStatus, FindingSectionStatus, Sections, CapOverallStatuses } from 'src/components/CapBox/constants';
import { notification } from 'src/components/Notification';
import s from 'src/components/CapField/CapEditField.styles';

const CapMultiButtonSwitchField = ({
  editable,
  isCriticalOrMajor,
  onSave,
  section,
  value,
}) => {
  const [editing, setEditing] = useState(false);
  const { t } = useTranslation();

  const onClickSave = async newStatus => {
    if (isCriticalOrMajor && newStatus === FindingStatus.NotRequired) {
      notification.error(
        '[Warning] You are setting a Critical or Major finding as optional.',
      );
    }
    await onSave(newStatus);
    setEditing(false);
  };

  const onClickEdit = async () => {
    if (!editable) {
      return;
    }
    await setEditing(!editing);
  };

  const StatusButton = props => {
    const {
      value: statusButtonValue,
      editable: statusButtonEditable,
      ...rest
    } = props;
    const name =
      statusButtonValue === 'Not Required'
        ? 'notRequired'
        : statusButtonValue.toLowerCase().replace(/ /gi, '-');
    const buttonTag =
      statusButtonValue === 'Not Required'
        ? 'pending'
        : statusButtonValue.toLowerCase().replace(/ /gi, '-');

    const colorStyle = {};
    colorStyle[name] = true;

    return (
      <Fragment {...rest}>
        <s.StatusSpan pointer={statusButtonEditable} {...colorStyle}>
          {section !== Sections.CapOverallStatus ? <s.StatusSvg key={name} name={`cap-${buttonTag}`} /> : null}
          {t(statusButtonValue.toUpperCase().replace(/ /gi, '_'))}
        </s.StatusSpan>
      </Fragment>
    );
  };

  StatusButton.propTypes = {
    value: PropTypes.string,
    editable: PropTypes.bool,
  };

  const statusOptions = useMemo(() => {
    switch (section) {
      case Sections.Finding:
        return Object.values(FindingSectionStatus);
      case Sections.CapOverallStatus:
        return Object.values(CapOverallStatuses);
      case Sections.Action:
        return Object.values(FindingStatus);
      default:
        return Object.values(FindingStatus);
    }
  }, [section, value]);

  const index = statusOptions.indexOf(value);
  if (index !== -1) statusOptions.splice(index, 1);

  return (
    <Fragment>
      <s.EditDiv
        editable={editable}
        onClick={onClickEdit}
        relative
        vertical-center
      >
        <StatusButton
          editable={editable}
          value={value === FindingStatus.NotRequired ? 'Not Required' : value}
        />
        {editing && (
          <s.StatusSwitchesBlock>
            {statusOptions.map(v => (
              <s.StatusSwitchDiv key={v} onClick={() => onClickSave(v)}>
                <StatusButton
                  value={v === FindingStatus.NotRequired ? 'Not Required' : v}
                />
              </s.StatusSwitchDiv>
            ))}
          </s.StatusSwitchesBlock>
        )}
      </s.EditDiv>
    </Fragment>
  );
};

CapMultiButtonSwitchField.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func,
  editable: PropTypes.bool,
  isCriticalOrMajor: PropTypes.bool,
  section: PropTypes.string,
};

CapMultiButtonSwitchField.defaultProps = {};

export default React.memo(CapMultiButtonSwitchField);
